import React from 'react';
import Logo from './Logo';
import { useSelector } from 'react-redux';

export default function Footer() {
    const { user } = useSelector((state) => state.user);
    const socialMedia = [
        {
            icon: "fab fa-linkedin-in",
            href: "https://www.linkedin.com/company/developmentanalyzer",
            label: "LinkedIn",
            gradient: "from-blue-600 to-blue-800",
            info: "Connect with us",
            infoIcon: "fas fa-briefcase",
            bgColor: "bg-blue-600",
            hoverBg: "hover:bg-blue-700",
            target: "_blank"
        },
        {
            icon: "fas fa-envelope",
            href: "mailto:support@privateopenhouse.com",
            label: "Email Us",
            gradient: "from-purple-600 to-purple-800",
            info: "support@privateopenhouse.com",
            infoIcon: "fas fa-at",
            bgColor: "bg-purple-600",
            hoverBg: "hover:bg-purple-700",
            minWidth: "min-w-[180px]"
        },
        {
            icon: "fas fa-phone",
            href: "tel:+16503880730",
            label: "Call Us",
            gradient: "from-pink-500 to-rose-700",
            info: "(650) 388-0730",
            infoIcon: "fas fa-phone-alt",
            bgColor: "bg-gradient-to-r from-pink-500 to-rose-600",
            hoverBg: "",
            minWidth: "min-w-[140px]"
        },
    ];

    return (
        <div className="relative border-t-4 border-blue-500 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 overflow-hidden opacity-40">
                <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-gradient-to-br from-blue-200 to-purple-200 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 h-96 w-96 rounded-full bg-gradient-to-tr from-pink-200 to-orange-200 blur-3xl"></div>

                {/* Floating Dots Pattern */}
                <svg className="absolute inset-0 h-full w-full opacity-20" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                            <circle cx="2" cy="2" r="1" fill="#3b82f6" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#dots)" />
                </svg>
            </div>

            <div className="relative mx-auto container px-2 py-10 lg:px-12 lg:py-16">
                {/* Main Footer Content */}
                <div className="mb-12 grid gap-8 lg:grid-cols-3">
                    {/* Brand Section */}
                    <div className="text-center lg:text-left space-y-4">
                        <div className="inline-block">
                            <Logo className="h-12" />
                        </div>
                        <p className="text-base text-gray-700 max-w-sm mx-auto lg:mx-0 leading-relaxed font-medium">
                            We are honored to provide developers, architects, and homeowners with the most sophisticated land analysis tools across California. Elevate your planning with official, real-time GIS insights.
                        </p>

                        {/* Feature Badges */}
                        <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                            <span className="inline-flex items-center gap-1 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-600 shadow-sm border border-blue-200">
                                <i className="fas fa-shield-alt"></i> Secure
                            </span>
                            <span className="inline-flex items-center gap-1 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold text-purple-600 shadow-sm border border-purple-200">
                                <i className="fas fa-bolt"></i> Fast
                            </span>
                            <span className="inline-flex items-center gap-1 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold text-pink-600 shadow-sm border border-pink-200">
                                <i className="fas fa-star"></i> Reliable
                            </span>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="text-center lg:text-left space-y-4">
                        <div className="inline-block">
                            <h3 className="text-2xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                                Quick Links
                            </h3>
                            <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full mt-2 shadow-md"></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                {
                                    name: "Privacy Policy",
                                    href: "/privacy-policy",
                                    icon: "fas fa-shield-alt",
                                    color: "blue"
                                },
                                {
                                    name: "Terms of Service",
                                    href: "/terms-of-service",
                                    icon: "fas fa-file-contract",
                                    color: "purple"
                                },
                                {
                                    name: "Admin Portal",
                                    href: "/admin",
                                    icon: "fas fa-shield-alt",
                                    color: "indigo"
                                },
                            ].filter(link => {
                                if (link.href === '/admin') {
                                    return user?.access_role === 'admin';
                                }
                                return true;
                            }).map((link, i) => (
                                <a
                                    key={i}
                                    href={link.href}
                                    className={`group flex items-center gap-3 rounded-xl bg-white/80 backdrop-blur-sm px-5 py-3 text-sm font-semibold text-gray-700 transition-all hover:bg-white hover:text-${link.color}-600 hover:scale-105 hover:shadow-lg border border-gray-200 hover:border-${link.color}-300`}
                                >
                                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-${link.color}-100 text-${link.color}-600 transition-colors group-hover:bg-${link.color}-600 group-hover:text-white`}>
                                        <i className={link.icon}></i>
                                    </div>
                                    {link.name}
                                    <i className="fas fa-arrow-right ml-auto opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0"></i>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Social Media Section */}
                    <div className="text-center lg:text-right space-y-4">
                        <div className="inline-block">
                            <h3 className="text-2xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                                Connect With Us
                            </h3>
                            <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full mt-2 shadow-md"></div>
                        </div>

                        <div className="flex items-center justify-center lg:justify-end gap-4 flex-wrap">
                            {socialMedia.map((social, index) => (
                                <div key={index} className="group relative">
                                    <a
                                        href={social.href}
                                        target={social.target}
                                        className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl ${social.bgColor} ${social.hoverBg} shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:-rotate-6`}
                                    >
                                        <i className={`${social.icon} text-2xl text-white`}></i>

                                        {/* Animated Ring */}
                                        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${social.gradient} opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-50 animate-pulse`}></div>
                                    </a>

                                    {/* Tooltip */}
                                    <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 transform opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:-translate-y-2">
                                        <div className="relative rounded-2xl bg-white shadow-2xl border-2 border-gray-200 p-4 min-w-fit whitespace-nowrap">
                                            <div className="text-sm font-bold text-gray-800">
                                                {social.label}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                                                <i className={`${social.infoIcon} ${social.bgColor.includes('gradient') ? 'text-pink-500' : social.bgColor.replace('bg-', 'text-')}`}></i>
                                                <span className="font-semibold">{social.info}</span>
                                            </div>
                                            {/* Arrow */}
                                            <div className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 transform bg-white border-r-2 border-b-2 border-gray-200"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="text-sm text-gray-600 font-medium flex items-center justify-center lg:justify-end gap-2">
                            <i className="fas fa-users text-blue-500"></i>
                            Join our growing community of 38K+ users
                        </p>
                    </div>
                </div>

                {/* Newsletter Section */}
                {/* <div className="mb-12 rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-400 p-8 shadow-2xl">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                        <div className="text-center lg:text-left">
                            <h4 className="text-2xl font-black text-white mb-2">
                                Stay Updated!
                            </h4>
                            <p className="text-blue-100 font-medium">
                                Get the latest updates and insights delivered to your inbox
                            </p>
                        </div>
                        <div className="flex gap-2 w-full lg:w-auto">
                            <input
                                type="email"
                                placeholder="Enter your email"
                                className="flex-1 lg:w-80 rounded-xl px-5 py-3 text-gray-700 font-medium placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/50 shadow-lg"
                            />
                            <button className="bg-white text-blue-600 font-bold px-6 py-3 rounded-xl hover:bg-blue-50 transition-all hover:scale-105 shadow-lg whitespace-nowrap">
                                <i className="fas fa-paper-plane mr-2"></i>
                                Subscribe
                            </button>
                        </div>
                    </div>
                </div> */}

                {/* Legal Disclaimer Section */}
                <div className="mb-12 p-8 bg-white/40 backdrop-blur-md rounded-3xl border border-blue-100 shadow-inner">
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600">
                            <i className="fas fa-gavel text-xl"></i>
                        </div>
                        <div className="space-y-3">
                            <h4 className="text-lg font-black text-slate-900 tracking-tight">Legal Disclaimer</h4>
                            <p className="text-sm text-gray-600 leading-relaxed font-medium">
                                DevelopmentAnalyzer is a preliminary data analysis platform. All AI-generated outputs, including buildability reports and GIS interpretations, are for <strong>informational purposes only</strong>. We do not provide architectural, engineering, or legal advice. Users are strictly advised to consult with licensed professionals before taking any action or making financial commitments based on our data. Accuracy of municipal GIS data is subject to jurisdictional updates and third-party reliability.
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed font-medium">
                                The Development Analyzer uses AI. We do not warrant that the services will be uninterrupted, accurate or error free. You accept and agree that any use of outputs from our service is at your sole risk and you will not rely on output as a sole source of truth or factual information, or as a substitute for professional advice.
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed font-medium">
                                The intended use of this product is to aid in the decision to invest further into a property's development potential. In no way does this tool replace the actions and outcomes of enlisting professional services that are traditionally used in real estate development.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bottom Copyright Bar */}
                <div className="border-t-2 border-gray-300 pt-8">
                    <div className="flex flex-col items-center justify-center gap-4 lg:flex-row">
                        <div className="text-center text-sm text-gray-600 lg:text-left font-medium">
                            <span className="inline-flex items-center gap-2">
                                <i className="far fa-copyright text-blue-500"></i>
                                <span className="font-black text-slate-900">
                                    {new Date().getFullYear()} Development<span className="text-blue-700">Analyzer</span>                            </span>
                            </span>
                            <span className="mx-2 text-gray-400">•</span>
                            <span className="text-gray-600">
                                All rights reserved. Made with
                                <i className="fas fa-heart text-red-500 mx-1"></i>
                                for innovation
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* FontAwesome CDN */}
            <link
                rel="stylesheet"
                href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
            />
        </div>
    );
}
